import { Fragment, useState } from "react";
import { Accept, FileError, FileRejection, useDropzone } from "react-dropzone";
import { Pressable, StyleSheet, View } from "react-native";
import { P, match } from "ts-pattern";
import { animDuration, colors, invariantColor, radii } from "../utils/design";
import { t } from "../utils/i18n";
import { Box } from "./Box";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Space } from "./Space";
import { Tag } from "./Tag";
import { Label, Text } from "./Text";

const styles = StyleSheet.create({
  container: {
    width: "100%",
    height: 210,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.neutral[600],
    borderStyle: "dashed",
    borderRadius: 4,
    backgroundColor: colors.neutral[800],
    transitionDuration: animDuration[200],
    transitionProperty: "border-color, background-color",
    outlineWidth: 0,
  },
  containerSmall: {
    height: "initial",
    paddingHorizontal: 24,
    paddingVertical: 24,
    alignItems: "flex-start",
  },
  containerHovered: {
    borderColor: colors.neutral[200],
  },
  containerPressed: {
    backgroundColor: colors.neutral[700],
  },
  containerError: {
    borderColor: colors.negative[500],
  },
  containerDragActive: {
    backgroundColor: colors.primary[900],
    borderColor: colors.primary[500],
  },
  containerWithFile: {
    borderStyle: "solid",
  },

  iconContainer: {
    width: 48,
    height: 48,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.neutral[900],
    borderRadius: radii[4],
  },
});

export type FileType = "image" | "document";

type Props = {
  label?: string;
  value: File | null;
  fileType: FileType;
  maxSize?: number;
  smallUI?: boolean;
  onChange: (file: File | null) => void;
};

const accept: Record<FileType, Accept> = {
  image: {
    "image/*": [".jpg", ".jpeg", ".png", ".webp"],
  },
  document: {
    "application/pdf": [".pdf"],
  },
};

const getAcceptedExtensions = (fileType: FileType) => {
  return Object.keys(accept[fileType])
    .reduce<string[]>((acc, key) => {
      // @ts-expect-error we're sure that key exists in accept[fileType]
      const extensions: string[] = accept[fileType][key];
      return [...acc, ...extensions];
    }, [])
    .filter((ext) => ext !== ".jpeg")
    .map((ext) => ext.slice(1));
};

const stringifyBytes = (bytes: number) => {
  if (bytes < 1024) {
    return `${bytes} o`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} Ko`;
  }
  return `${(bytes / 1024 / 1024).toFixed(2)} Mo`;
};

const DEFAULT_MAX_SIZE = 1024 * 1024; // 1MB

export const InputFile: React.FC<Props> = ({
  label,
  value,
  fileType,
  maxSize = DEFAULT_MAX_SIZE,
  smallUI = false,
  onChange,
}) => {
  const [errors, setErrors] = useState<FileError[]>([]);
  const hasFile = value != null;

  const onDrop = ([file]: File[]) => {
    if (file != null) {
      setErrors([]);
      onChange(file);
    }
  };

  const onDropRejected = (rejections: FileRejection[]) => {
    setErrors(rejections.flatMap((rejection) => rejection.errors));
  };

  const hasInvalidType = errors.some((error) => error.code === "file-invalid-type");
  const hasTooBigFile = errors.some((error) => error.code === "file-too-large");

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    disabled: hasFile,
    onDropRejected,
    onDrop,
    accept: accept[fileType],
    maxFiles: 1,
    maxSize,
  });
  return (
    <>
      {label != null && (
        <>
          <Label>{label}</Label>
          <Space height={8} />
        </>
      )}

      <div {...getRootProps()} tabIndex={-1}>
        <input {...getInputProps()} />
        <Pressable
          style={({ focused, hovered, pressed }) => [
            styles.container,
            smallUI && styles.containerSmall,
            (focused || hovered) && styles.containerHovered,
            pressed && styles.containerPressed,
            errors.length > 0 && styles.containerError,
            isDragActive && styles.containerDragActive,
            hasFile && styles.containerWithFile,
          ]}
          disabled={hasFile}
          onPress={open}
        >
          {match({ value, isDragActive })
            .with({ value: P.not(P.nullish) }, ({ value }) => (
              <Box direction={smallUI ? "row" : "column"} alignItems="center">
                <View style={styles.iconContainer}>
                  <Icon name="file_check" size={24} color={colors.primary[500]} />
                </View>

                <Space width={12} height={12} />

                <Box direction="column" alignItems={smallUI ? "flex-start" : "center"}>
                  <Text variant="small" color={invariantColor.white}>
                    {value.name}
                  </Text>
                  <Pressable onPress={() => onChange(null)}>
                    <Text variant="small" color={colors.primary[500]}>
                      {t("component.dropzone.delete_file")}
                    </Text>
                  </Pressable>
                </Box>
              </Box>
            ))
            .with({ isDragActive: true }, () => (
              <Text variant="small" color={colors.primary[500]}>
                {t("component.dropzone.drop_here")}
              </Text>
            ))
            .otherwise(() => (
              <Box direction="column" alignItems={smallUI ? "flex-start" : "center"}>
                {smallUI ? (
                  <Button variant="outline" onPress={open}>
                    {t("component.dropzone.select_file_short")}
                  </Button>
                ) : (
                  <View style={styles.iconContainer}>
                    <Icon name="file_upload" size={24} color={invariantColor.white} />
                  </View>
                )}

                {smallUI === false && (
                  <>
                    <Space height={12} />

                    <Text variant="small" color={invariantColor.white}>
                      {t("component.dropzone.drag_drop")}
                    </Text>
                    <Text variant="small" color={colors.neutral[50]}>
                      {t("common.or")}&nbsp;
                      <Text color={colors.primary[500]}>{t("component.dropzone.select_file")}</Text>
                    </Text>
                  </>
                )}

                <Space height={12} />

                <Box direction="row" alignItems="center">
                  <Text
                    variant="smallFlat"
                    color={hasInvalidType ? colors.negative[500] : colors.neutral[200]}
                  >
                    {t("component.dropzone.formats")}
                  </Text>
                  {getAcceptedExtensions(fileType).map((format) => (
                    <Fragment key={format}>
                      <Space width={4} />
                      <Tag variant={hasInvalidType ? "negative" : "neutral"} uppercase={true}>
                        {format}
                      </Tag>
                    </Fragment>
                  ))}
                </Box>

                <Space height={8} />

                <Text
                  variant="smallFlat"
                  color={hasTooBigFile ? colors.negative[500] : colors.neutral[200]}
                >
                  {t("component.dropzone.max_size", {
                    size: stringifyBytes(maxSize).replace(".00", ""),
                  })}
                </Text>
              </Box>
            ))}
        </Pressable>
      </div>
    </>
  );
};
